import type { PayrollEntryDetailsFragment } from "../../../../lib/graphql/generated";
import type { CheckSetupValues } from "./CheckSetup";
import type { useFormatMoney } from "../../../../lib/formats/money";
import {
  CHECK_TEMPLATE_FOOTER,
  CHECK_TEMPLATE_HEADER,
  CHECK_TEMPLATE_PAGE_PROTOTYPE,
  CHECK_TEMPLATE_PAGE_SEPARATOR,
} from "./templates/checkTemplateParts";
import {
  buildCheckRenderContexts,
  buildVendorCheckRenderContext,
  type CheckTemplateFields,
  type VendorCheckData,
} from "./checkRenderData";

type FormatMoneyFn = ReturnType<typeof useFormatMoney>;

interface RenderChecksRtfParams {
  entry: PayrollEntryDetailsFragment;
  setupValues: CheckSetupValues;
  formatMoney: FormatMoneyFn;
}

export function renderChecksRtf({
  entry,
  setupValues,
  formatMoney,
}: RenderChecksRtfParams) {
  const contexts = buildCheckRenderContexts({
    entry,
    setupValues,
    formatMoney,
  });

  const pages = contexts
    .map(({ fields }) =>
      applyTemplate(CHECK_TEMPLATE_PAGE_PROTOTYPE, escapeFields(fields))
    )
    .join(CHECK_TEMPLATE_PAGE_SEPARATOR);

  return `${CHECK_TEMPLATE_HEADER}${pages}${CHECK_TEMPLATE_FOOTER}`;
}

interface RenderVendorCheckRtfParams {
  data: VendorCheckData;
  formatMoney: FormatMoneyFn;
  template: string;
}

export function renderVendorCheckRtf({
  data,
  formatMoney,
  template,
}: RenderVendorCheckRtfParams) {
  const context = buildVendorCheckRenderContext(data, formatMoney);

  const page = applyTemplate(template, escapeFields(context.fields));

  return `${CHECK_TEMPLATE_HEADER}${page}${CHECK_TEMPLATE_FOOTER}`;
}

function escapeFields(fields: CheckTemplateFields): Record<string, string> {
  return Object.entries(fields).reduce<Record<string, string>>(
    (result, [key, value]) => {
      result[key] = escapeRtf(value);
      return result;
    },
    {}
  );
}

const CP1252_HEX_OVERRIDES: Record<string, string> = {
  "\u00e1": "e1",
  "\u00c1": "c1",
  "\u00e9": "e9",
  "\u00c9": "c9",
  "\u00ed": "ed",
  "\u00cd": "cd",
  "\u00f3": "f3",
  "\u00d3": "d3",
  "\u00fa": "fa",
  "\u00da": "da",
  "\u00f1": "f1",
  "\u00d1": "d1",
  "\u00fc": "fc",
  "\u00dc": "dc",
  "\u00bf": "bf",
  "\u00a1": "a1",
};

function applyTemplate(template: string, values: Record<string, string>) {
  return template.replace(/{{([A-Z_]+)}}/g, (_match, key) => values[key] ?? "");
}

function escapeRtf(value: string) {
  if (!value) return "";

  const normalized = value.normalize("NFC");

  let result = "";

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];

    switch (char) {
      case "\\":
        result += "\\\\";
        break;
      case "{":
        result += "\\{";
        break;
      case "}":
        result += "\\}";
        break;
      case "\n":
        result += "\\par ";
        break;
      default: {
        const code = char.charCodeAt(0);
        if (code > 126 || CP1252_HEX_OVERRIDES[char]) {
          const hex =
            CP1252_HEX_OVERRIDES[char] ?? code.toString(16).padStart(2, "0");
          if (hex.length === 2) {
            result += `\\'${hex}`;
          } else {
            result += `\\u${code}?`;
          }
        } else {
          result += char;
        }
      }
    }
  }

  return result;
}
